import {
  BloomMergePostProcess,
  BlurPostProcess,
  Camera,
  ExtractHighlightsPostProcess,
  type Nullable,
  Observer,
  PostProcess,
  PostProcessRenderEffect,
  Scene,
  Texture,
  Vector2,
} from '@babylonjs/core';

import { onResize } from '../../observable';

import { BloomOptions } from './PostProcess';

export class BloomEffect extends PostProcessRenderEffect {
  private scene: Scene;
  private _options: BloomOptions;
  public get options() {
    return this._options;
  }

  private _effects: Array<PostProcess> = [];
  public get effects() {
    return this._effects;
  }

  private _downscale: ExtractHighlightsPostProcess;
  public get downscale() {
    return this._downscale;
  }

  private blurX: BlurPostProcess;
  private blurY: BlurPostProcess;
  private merge: BloomMergePostProcess;

  private resizeObserver: Nullable<Observer<void>> = null;

  constructor(scene: Scene, options: BloomOptions, pipelineTextureType = 0, blockCompilation = false) {
    super(
      scene.getEngine(),
      'bloom',
      () => {
        return this._effects;
      },
      true
    );
    this.scene = scene;
    this._options = options;

    this.resizeObserver = onResize.add(() => {
      this.updateOptions();
    });

    this._downscale = new ExtractHighlightsPostProcess(
      'highlights',
      1.0,
      null,
      Texture.BILINEAR_SAMPLINGMODE,
      scene.getEngine(),
      false,
      pipelineTextureType,
      blockCompilation
    );

    this.blurX = new BlurPostProcess(
      'horizontal blur',
      new Vector2(1.0, 0),
      10.0,
      this._options.scale,
      null,
      Texture.BILINEAR_SAMPLINGMODE,
      scene.getEngine(),
      false,
      pipelineTextureType,
      undefined,
      blockCompilation
    );
    this.blurX.alwaysForcePOT = true;
    this.blurX.autoClear = false;

    this.blurY = new BlurPostProcess(
      'vertical blur',
      new Vector2(0, 1.0),
      10.0,
      this._options.scale,
      null,
      Texture.BILINEAR_SAMPLINGMODE,
      scene.getEngine(),
      false,
      pipelineTextureType,
      undefined,
      blockCompilation
    );
    this.blurY.alwaysForcePOT = true;
    this.blurY.autoClear = false;

    this._effects = [this._downscale, this.blurX, this.blurY];

    this.merge = new BloomMergePostProcess(
      'bloomMerge',
      this._downscale,
      this.blurY,
      this._options.weight,
      this._options.scale,
      null,
      Texture.BILINEAR_SAMPLINGMODE,
      scene.getEngine(),
      false,
      pipelineTextureType,
      blockCompilation
    );
    this.merge.autoClear = false;
    this._effects.push(this.merge);
  }

  public disposeEffects(camera: Camera) {
    for (let effectIndex = 0; effectIndex < this._effects.length; effectIndex++) {
      this._effects[effectIndex]?.dispose(camera);
    }
  }
  public dispose() {
    if (this.resizeObserver) {
      onResize.remove(this.resizeObserver);
      this.resizeObserver = null;
    }
  }
  public updateEffects() {
    for (let effectIndex = 0; effectIndex < this._effects.length; effectIndex++) {
      this._effects[effectIndex]?.updateEffect();
    }
  }

  public isReady() {
    return this._effects.every((effect) => effect.isReady());
  }

  public updateOptions(options?: Partial<BloomOptions>) {
    this._options = { ...this._options, ...options };

    const hardwareScaleLevel = this.scene.getEngine().getHardwareScalingLevel();
    const kernel = this._options.kernel / hardwareScaleLevel;
    this.blurX.kernel = kernel;
    this.blurY.kernel = kernel;
    this._downscale.threshold = this._options.threshold;
    this.merge.weight = this._options.weight;
  }
}
